import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/libs/shadcn";
import type { ComponentProps } from "react";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-[11px] font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/30 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-ink text-primary-foreground hover:bg-ink/90",
        dark: "bg-ink text-primary-foreground hover:bg-ink/90",
        coral: "bg-coral text-ink hover:bg-coral/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border border-line bg-card text-ink hover:border-[#cbcdc5] hover:bg-soft",
        secondary: "bg-cream text-secondary-foreground hover:bg-cream/80",
        soft: "bg-soft text-ink hover:bg-[#ecefe8]",
        ghost: "text-ink hover:bg-cream",
        link: "text-coral-deep underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-3.5 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-lg gap-1.5 px-2.5 text-[10px] has-[>svg]:px-2",
        lg: "h-11 rounded-[11px] px-5 text-xs has-[>svg]:px-4",
        icon: "size-10",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
